<script setup>
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { computed } from 'vue'

import { useStatementUploadDestination } from '~/composables/useStatementUploadDestination'
import { statementCountLabel } from '~/libs/document-helper'
import { useBankConnectorStore } from '~/store/bank-connector'
import { useBankPullStatusUiStore } from '~/store/bank-pull-status-ui'

const bankConnectorStore = useBankConnectorStore()
const bankPullStatusUiStore = useBankPullStatusUiStore()
const { destination, disableManualBankStatement, requestUpload } = useStatementUploadDestination()

const HEADER_COPY = {
  pending: { text: 'Retrieving your statements...', class: 'tw:text-w-neutral-900' },
  completed: { text: 'All accounts connected', class: 'tw:text-w-neutral-900' },
  failed: { text: 'We hit a problem', class: 'tw:text-red-300' },
}

const RETRIEVAL_COPY = {
  pending: 'Retrieving statements',
  completed: 'Statements retrieved',
  failed: 'Retrieval failed',
}

// The header summarizes the same indicatorState that drives the chip icon - never a local
// re-derivation over the per-account rows below (BR-9).
const header = computed(() => HEADER_COPY[bankPullStatusUiStore.indicatorState] ?? null)

// BR-9: reads the same per-account classifier the bank-details page uses - never recomputes it.
// A failed account's row carries its statement count, and reads as uploaded once every
// statement it was missing is in (BR-11) - getAccountPullState itself stays 'failed'.
const accountsWithPullState = computed(() => bankConnectorStore.normalizedCheckingAccounts.map((account) => {
  const pullState = bankConnectorStore.getAccountPullState(account.accountId)
  const statementsNeeded = bankPullStatusUiStore.statementsNeededByAccountId[account.accountId]
  if (pullState === 'failed' && statementsNeeded === 0) {
    return { ...account, pullState, displayState: 'completed', retrievalCopy: 'Statements uploaded' }
  }
  const retrievalCopy = pullState === 'failed' && statementsNeeded > 0
    ? `${RETRIEVAL_COPY.failed} · ${statementCountLabel(statementsNeeded)} needed`
    : RETRIEVAL_COPY[pullState]
  return { ...account, pullState, displayState: pullState, retrievalCopy }
}))

const uploadActionCopy = computed(() => {
  const total = bankPullStatusUiStore.statementsNeededTotal
  return total > 0 ? `Upload ${statementCountLabel(total)}` : 'Upload statements'
})

// The CTA is gated on the same indicatorState that turned the chip red, not a local
// re-derivation over per-account rows - indicatorState already applies the shared
// promptEligible guard chain (approvals, mpApplicationComplete, manual-upload-already-done) and
// covers failure cases a per-account scan alone would miss (e.g. no eligible accounts to pull
// from at all). A narrower local definition risks disagreeing with the banner/chip on identical
// data.
const showUploadAction = computed(() => (
  bankPullStatusUiStore.indicatorState === 'failed' && !disableManualBankStatement.value
))
</script>

<template>
  <!-- Accents come from the semantic Tailwind tokens (link, and primary for the spinners), which
       libs/tailwind-affiliate-colors.js re-points at the partner's colors. This panel is teleported
       onto <body>, outside the layout container carrying those vars, so BankPullStatusChip binds
       them onto the panel itself. The font follows the partner's too. Text deliberately stays on
       the fixed neutral palette rather than text-primary: the panel is always white, and a
       partner's body text color is chosen for its own page background (white text on a dark
       theme would vanish here). -->
  <div
    class="tw:flex tw:min-w-64 tw:flex-col tw:p-3 tw:font-(family-name:--app-font-family-body)"
    data-cy="bank-pull-status-popover"
  >
    <div
      v-if="header"
      class="tw:flex tw:items-center tw:gap-2 tw:pb-3"
      data-cy="bank-pull-status-popover-header"
    >
      <BankPullStatusIcon :state="bankPullStatusUiStore.indicatorState" />
      <span class="tw:type-500 tw:font-bold" :class="header.class">{{ header.text }}</span>
    </div>
    <hr v-if="header && accountsWithPullState.length > 0" class="tw:mb-3 tw:w-full tw:border! tw:border-w-neutral-100!">
    <template v-for="(account, index) in accountsWithPullState" :key="account.accountId">
      <hr v-if="index > 0" class="tw:my-3 tw:w-full tw:border! tw:border-w-neutral-100!">
      <div class="tw:flex tw:flex-col tw:gap-1" :data-cy="`bank-pull-status-popover-account-${account.accountId}`">
        <span class="tw:type-500 tw:font-bold tw:text-w-neutral-900">
          {{ account.bankName }} &middot; {{ account.accountName }} **{{ account.mask }}
        </span>
        <div class="tw:flex tw:items-center tw:gap-2" :data-cy="`account-connected-row-${account.accountId}`">
          <font-awesome-icon :icon="faCheck" class="green-300" />
          <span class="type-500">Connected</span>
        </div>
        <div v-if="account.pullState !== null" class="tw:flex tw:items-center tw:gap-2" :data-cy="`account-retrieving-row-${account.accountId}`">
          <BankPullStatusIcon :state="account.displayState" :data-cy="`account-retrieving-icon-${account.accountId}`" />
          <span class="type-500">{{ account.retrievalCopy }}</span>
        </div>
      </div>
    </template>
    <hr v-if="showUploadAction && accountsWithPullState.length > 0" class="tw:mt-3 tw:w-full tw:border! tw:border-w-neutral-100!">
    <a
      v-if="showUploadAction"
      :href="destination"
      class="tw:type-500 tw:pt-3 tw:text-link tw:underline"
      data-cy="bank-pull-status-popover-upload-link"
      @click.prevent="requestUpload"
    >
      {{ uploadActionCopy }}
    </a>
  </div>
</template>

